'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';

const lessons = [
  {
    id: 'lesson-001',
    subject: "Tajweed",
    title: 'Ghunnah Rules — Noon & Meem Mushaddad',
    icon: '🎵',
    duration: '25 min',
    type: 'Audio + Exercise',
    status: 'in-progress' as const,
    progress: 60,
    dueLabel: 'Due today',
    color: 'bg-purple/10 border-purple/20',
    dotColor: 'bg-purple',
  },
  {
    id: 'lesson-002',
    subject: "Qur\'anic Arabic",
    title: 'Root Words: ك-ت-ب (to write) — Derivatives & Usage',
    icon: '📝',
    duration: '30 min',
    type: 'Reading + Quiz',
    status: 'locked' as const,
    progress: 0,
    dueLabel: 'Unlocks after Tajweed',
    color: 'bg-muted border-border',
    dotColor: 'bg-muted-foreground',
  },
  {
    id: 'lesson-003',
    subject: "Fiqh Foundations",
    title: 'Conditions of Valid Wudu — Detailed Study',
    icon: '💧',
    duration: '20 min',
    type: 'Video + Reflection',
    status: 'mastered' as const,
    progress: 100,
    dueLabel: 'Completed yesterday',
    color: 'bg-green-50 border-green-200',
    dotColor: 'bg-primary',
  },
  {
    id: 'lesson-004',
    subject: "Seerah",
    title: "The Year of the Elephant — Seerah Adventure Chapter 3",
    icon: '🐘',
    duration: '35 min',
    type: 'Interactive Story',
    status: 'in-progress' as const,
    progress: 30,
    dueLabel: 'Due Friday',
    color: 'bg-amber-50 border-amber-200',
    dotColor: 'bg-amber-400',
  },
  {
    id: 'lesson-005',
    subject: "Islamic Ethics",
    title: 'Honesty in Daily Life — Ethical Scenarios',
    icon: '⚖️',
    duration: '15 min',
    type: 'Scenario Game',
    status: 'locked' as const,
    progress: 0,
    dueLabel: 'Due next Monday',
    color: 'bg-muted border-border',
    dotColor: 'bg-muted-foreground',
  },
  {
    id: 'lesson-006',
    subject: "Aqidah",
    title: 'The Six Pillars of Iman — Review & Assessment',
    icon: '🌟',
    duration: '20 min',
    type: 'Assessment',
    status: 'pending' as const,
    progress: 0,
    dueLabel: 'Due tomorrow',
    color: 'bg-blue-50 border-blue-200',
    dotColor: 'bg-sky',
  },
];

export default function TodayLessonQueue() {
  const [activeLesson, setActiveLesson] = useState<string | null>(null);

  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div>
          <h2 className="text-base font-700 text-foreground">Today's Lesson Queue</h2>
          <p className="text-xs text-muted-foreground mt-0.5">6 lessons · 2 in progress · 1 completed</p>
        </div>
        <button className="text-xs font-600 text-primary hover:underline">View Full Schedule</button>
      </div>
      <div className="divide-y divide-border">
        {lessons.map((lesson) => (
          <div
            key={lesson.id}
            className={`flex items-center gap-4 px-6 py-4 hover:bg-muted/40 transition-colors cursor-pointer group
              ${activeLesson === lesson.id ? 'bg-primary/5' : ''}
            `}
            onClick={() => setActiveLesson(activeLesson === lesson.id ? null : lesson.id)}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg border ${lesson.color} flex-shrink-0`}>
              {lesson.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-600 text-muted-foreground uppercase tracking-wide">{lesson.subject}</span>
                <StatusBadge variant={lesson.status} label={lesson.status.replace('-', ' ')} />
              </div>
              <p className="text-sm font-600 text-foreground truncate">{lesson.title}</p>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Icon name="ClockIcon" size={12} />
                  {lesson.duration}
                </span>
                <span className="text-xs text-muted-foreground">{lesson.type}</span>
                <span className="text-xs text-muted-foreground">{lesson.dueLabel}</span>
              </div>
              {lesson.progress > 0 && lesson.progress < 100 && (
                <div className="mt-2 w-full bg-border rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${lesson.dotColor}`}
                    style={{ width: `${lesson.progress}%` }}
                  />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              {lesson.status !== 'locked' && lesson.status !== 'mastered' && (
                <button className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-600 hover:bg-primary/90 transition-colors active:scale-95">
                  {lesson.status === 'in-progress' ? 'Continue' : 'Start'}
                </button>
              )}
              {lesson.status === 'mastered' && (
                <button className="px-3 py-1.5 bg-muted text-muted-foreground rounded-lg text-xs font-600">
                  Review
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}